import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks';
import { useToast } from '@/components/common';
import { InlineLoader } from '@shared/ui/loading';
import { handleOAuthCallback } from '@/utils/oauth';
import { referralService } from '@/services/referral.service';

export function OAuthCallbackPage() {
  const navigate = useNavigate();
  const { loginWithGoogle, loginWithLinkedIn } = useAuth();
  const toast = useToast();
  const processedRef = useRef(false);

  useEffect(() => {
    // Prevent double-execution (React 18 StrictMode or re-renders)
    if (processedRef.current) return;

    const params = new URLSearchParams(window.location.search);
    if (!params.has('code')) return;

    processedRef.current = true;

    const processCallback = async () => {
      try {
        const result = handleOAuthCallback();

        if (!result) {
          toast.error('OAuth authentication failed');
          navigate('/login', { replace: true });
          return;
        }

        const { code, provider } = result;

        if (provider === 'google') {
          await loginWithGoogle(code);
        } else if (provider === 'linkedin') {
          await loginWithLinkedIn(code);
        } else {
          throw new Error('Unknown OAuth provider');
        }

        // Apply pending referral code from sessionStorage (set before OAuth redirect)
        try {
          const pendingRef = sessionStorage.getItem('pending_referral_code');
          if (pendingRef) {
            await referralService.applyCode(pendingRef);
            sessionStorage.removeItem('pending_referral_code');
          }
        } catch {
          // Non-critical — referral apply failed, user still logged in
        }

        toast.success('Successfully logged in!');
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Authentication failed');
        navigate('/login', { replace: true });
      }
    };

    processCallback();
  }, [navigate, loginWithGoogle, loginWithLinkedIn, toast]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <InlineLoader size="lg" />
        <p className="mt-4 text-text-secondary">Completing authentication...</p>
      </div>
    </div>
  );
}
